"use client";

import ContactInfo from "./ContactInfo";
import ContactForm from "./ContactForm";
import { GeneralData } from "@/types/components";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

interface ContactData {
  content: {
    tagline: string;
    title: string;
    description: string;
    form: {
      title: string;
      description: string;
    };
  };
}

export default function ContactSection({
  generalData,
  contactData,
}: {
  generalData: GeneralData["content"];
  contactData: ContactData["content"];
}) {
  const { tagline, title, description, form } = contactData;

  
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const taglineRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  
  useEffect(() => {
    const sectionElement = sectionRef.current;
    const headerElement = headerRef.current;
    const taglineElement = taglineRef.current;
    const titleElement = titleRef.current;
    const descriptionElement = descriptionRef.current;
    const contentElement = contentRef.current;

    if (!sectionElement) return;

    
    gsap.set([headerElement, contentElement], {
      opacity: 0,
      filter: "blur(8px)",
      y: 20,
    });

    gsap.set(taglineElement, {
      opacity: 0,
      y: 20,
      filter: "blur(8px)",
    });

    gsap.set(titleElement, {
      opacity: 0,
      y: 30,
      filter: "blur(8px)",
    });

    gsap.set(descriptionElement, {
      opacity: 0,
      y: 20,
      filter: "blur(8px)",
    });

    gsap.set(contentElement, {
      opacity: 0,
      y: 40,
      filter: "blur(8px)",
    });

    const ctx = gsap.context(() => {
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionElement,
          start: "top 80%",
          end: "top 40%",
          toggleActions: "play none none reverse",
        },
      });

      
      tl.to(headerElement, {
        opacity: 1,
        filter: "blur(0px)",
        duration: 0.6,
        ease: "power2.out",
      })
        .to(
          taglineElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.4"
        )
        .to(
          titleElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.6"
        )
        .to(
          descriptionElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.6,
            ease: "power2.out",
          },
          "-=0.4"
        )
        .to(
          contentElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1,
            ease: "power2.out",
          },
          "-=0.3"
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="contact-form" className="container py-[70px]">
      <div ref={headerRef} className="text-center mb-12 lg:mb-20">
        {tagline && (
          <div
            ref={taglineRef}
            className="flex items-center justify-center mb-4 opacity-0"
          >
            <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
            <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
              {tagline}
            </p>
          </div>
        )}
        {title && (
          <h2
            ref={titleRef}
            className="text-4xl md:text-5xl font-bold text-theme-secondary opacity-0"
          >
            {title}
          </h2>
        )}
        {description && (
          <p
            ref={descriptionRef}
            className="mt-4 text-lg text-theme-secondary-light opacity-0"
          >
            {description}
          </p>
        )}
      </div>

      <div
        ref={contentRef}
        className="grid lg:grid-cols-5 gap-12 items-start opacity-0"
      >
        <ContactInfo generalData={generalData} form={form} />
        <ContactForm />
      </div>
    </section>
  );
}
